import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { videoCategories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import FilterSidebar from "@/pages/category/components/FilterSidebar";
import { sortVideos, paginate, type SortKey } from "@/utils/videoQuery";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 9;

const SORTS: { key: SortKey; label: string }[] = [
  { key: "latest", label: "时间最新" },
  { key: "likes", label: "最多点赞" },
  { key: "comments", label: "最多评论" },
];

export default function CategoryPage() {
  const { categorySlug } = useParams();
  const category = videoCategories.find((item) => item.slug === categorySlug);

  const [subCategory, setSubCategory] = useState<string | null>(null);
  const [region, setRegion] = useState<string | null>(null);
  const [year, setYear] = useState<number | null>(null);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  usePageMeta(
    category ? `${category.name} - 樱桃视频` : "分类未找到 - 樱桃视频",
    category
      ? `樱桃视频提供丰富的${category.name}视频，助您轻松浏览精彩内容。`
      : "未找到该分类"
  );

  useEffect(() => {
    setSubCategory(null);
    setRegion(null);
    setYear(null);
    setSort("latest");
    setPage(1);
    setShowFilters(false);
  }, [categorySlug]);

  const categoryVideos = useMemo(
    () => videos.filter((video) => video.categorySlug === categorySlug),
    [categorySlug]
  );

  const regions = useMemo(
    () => Array.from(new Set(categoryVideos.map((video) => video.region))),
    [categoryVideos]
  );

  const years = useMemo(
    () => Array.from(new Set(categoryVideos.map((video) => video.year))).sort((a, b) => b - a),
    [categoryVideos]
  );

  const filtered = useMemo(() => {
    let list = categoryVideos;
    if (subCategory) list = list.filter((video) => video.subCategory === subCategory);
    if (region) list = list.filter((video) => video.region === region);
    if (year) list = list.filter((video) => video.year === year);
    return sortVideos(list, sort);
  }, [categoryVideos, subCategory, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = paginate(filtered, safePage, PAGE_SIZE);

  const resetFilters = () => {
    setSubCategory(null);
    setRegion(null);
    setYear(null);
    setPage(1);
  };

  if (!category) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <EmptyState
          message="未找到该分类"
          hint="该分类可能不存在或已被移除，试试其他分类吧。"
          actionHref="/"
          actionText="回到首页"
        />
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: category.name }]} />

      <header className="mt-5 max-w-3xl">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
          {category.name}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600">{category.description}</p>
      </header>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-8">
        <aside className="lg:col-span-1">
          <button
            type="button"
            onClick={() => setShowFilters((prev) => !prev)}
            className="mb-4 flex w-full items-center justify-between rounded-md border border-background-200 bg-background-50 px-4 py-3 lg:hidden cursor-pointer"
          >
            <span className="flex items-center gap-2 text-sm text-foreground-800">
              <i className="ri-filter-3-line"></i>
              筛选条件
            </span>
            <i className={`ri-arrow-down-s-line transition-transform ${showFilters ? "rotate-180" : ""}`}></i>
          </button>
          <div className={`${showFilters ? "block" : "hidden"} lg:block lg:sticky lg:top-24`}>
            <FilterSidebar
              subCategories={category.subCategories}
              subCategory={subCategory}
              regions={regions}
              region={region}
              years={years}
              year={year}
              onSubChange={(value) => {
                setSubCategory(value);
                setPage(1);
              }}
              onRegionChange={(value) => {
                setRegion(value);
                setPage(1);
              }}
              onYearChange={(value) => {
                setYear(value);
                setPage(1);
              }}
              onReset={resetFilters}
            />
          </div>
        </aside>

        <div className="lg:col-span-3">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <p className="text-sm text-foreground-500">
              共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 个视频
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">排序：</span>
              {SORTS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setSort(item.key);
                    setPage(1);
                  }}
                  className={`rounded-full px-3 py-1.5 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                    sort === item.key
                      ? "bg-secondary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {pageItems.length === 0 ? (
            <EmptyState
              message="未找到符合条件的视频"
              hint="换个筛选条件试试，或重置全部筛选。"
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-7">
              {pageItems.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          )}

          {pageItems.length === 0 && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1 rounded-md bg-primary-500 px-4 py-2 text-sm font-label font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}

          <Pagination current={safePage} total={totalPages} onChange={setPage} />
        </div>
      </div>
    </div>
  );
}